import { IGetBeneficiariesRequest } from '@smiles/voebiz-core-beneficiaries/dist/Types/BeneficiariesApi.types';
import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  IListPurchaseMilesParams,
  ITransactionPurchaseMiles,
} from '@smiles/voebiz-core-corporate-club/dist/Types/GetListPurchaseMiles.types';
import { Helpers } from '@smiles/voebiz-core-engine';
import { getMinAndMaxMiles, getTransactionsList } from '../../services/services';

const transactionsDefault = {
  transactions: [] as ITransactionPurchaseMiles[],
  milesRange: { minMiles: 0, maxMiles: 0 },
  isActive: { isActiveForClients: false, isActiveForTeam: false },
  loading: false,
  isEmpty: false,
  isLastPage: false,
  isEmptySearch: false,
  isEmptyFilter: false,
  isProcessing: false,
  rewardCustomersIsEnabled: false,
  setFilter: () => undefined,
  showMore: () => undefined,
  cpf: '',
  setCpf: () => undefined,
};

export interface ContextTransaction {
  transactions: ITransactionPurchaseMiles[] | undefined;
  milesRange: { minMiles; maxMiles };
  isActive: { isActiveForClients; isActiveForTeam };
  loading: boolean;
  isEmpty: boolean;
  isLastPage: boolean;
  isEmptyFilter: boolean;
  isEmptySearch: boolean;
  isProcessing: boolean;
  rewardCustomersIsEnabled: boolean;
  filter?: IGetBeneficiariesRequest;
  setFilter: (filter: IGetBeneficiariesRequest) => void;
  showMore: () => void;
  getListTransactions?: () => void;
  cpf: string;
  setCpf: (cpf: string) => void;
}

const TransactionsContext = createContext<Partial<ContextTransaction>>(transactionsDefault);

const TransactionsProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const [cpf, setCpf] = useState('');

  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();

  const page = searchParams.get('p');

  const urlCpf = searchParams.get('cpf');

  const isTransactionsList = page === null;

  const [isLastPage, setIsLastPage] = useState(false);

  const [transactions, setTransactions] = useState<ITransactionPurchaseMiles[]>();

  const [filter, setFilterState] = useState<IGetBeneficiariesRequest>({
    pageSize: 25,
  });

  const [milesRange, setMilesRange] = useState({ minMiles: 0, maxMiles: 0 });

  const [isActive, setIsActive] = useState({ isActiveForClients: false, isActiveForTeam: false });

  const [isProcessing, setIsProcessing] = useState(false);

  const enableRewardCustomers = Helpers?.getConstants('ENABLE_VOEBIZ_REWARD_CUSTOMERS');

  const rewardCustomersIsEnabled = typeof enableRewardCustomers === 'boolean' && enableRewardCustomers;

  const setFilter = useCallback((newFilter: IGetBeneficiariesRequest) => {
    setFilterState((prevFilter) => ({ ...prevFilter, ...newFilter }));
  }, []);

  const showMore = useCallback(() => {
    const pageSize = Number(filter?.pageSize ?? 10) + 25;

    const startRowNum = 0;

    setFilter({ pageSize, startRowNum });
  }, [filter?.pageSize, setFilter]);

  const getListTransactions = useCallback(() => {
    setLoading(true);

    getTransactionsList(filter as IListPurchaseMilesParams)
      .then(({ data }) => {
        if (!data) {
          setTransactions([]);
          return;
        }
        const response = data as unknown as {
          transactionsPurchaseMiles: ITransactionPurchaseMiles[];
          lastPage: boolean;
        };
        if (response) {
          setIsLastPage(response?.lastPage);
          setTransactions(response?.transactionsPurchaseMiles);
          setIsProcessing(response.transactionsPurchaseMiles.some((item) => item.status === 'Em processamento'));
        } else setTransactions([]);
      })
      // TO DO retirar o true
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    isTransactionsList && getListTransactions();
  }, [getListTransactions, isTransactionsList]);

  useEffect(() => {
    const fetchMilesRange = async () => {
      const result = await getMinAndMaxMiles();
      setMilesRange(result);
      setIsActive({ isActiveForClients: result.activeForClients, isActiveForTeam: result.activeForTeam });
    };
    fetchMilesRange();
  }, []);

  const isEmpty = !transactions || transactions.length === 0;

  const avaliableFilters = {
    status: filter?.status,
    createdStart: filter?.createdStart,
    createdEnd: filter?.createdEnd,
    birthStart: filter?.birthStart,
    birthEnd: filter?.birthEnd,
  };

  const isEmptyFilter = !Object.values({ ...avaliableFilters }).some((value) => value);

  const isEmptySearch = !filter?.search;

  const value = useMemo(
    () => ({
      setFilter,
      showMore,
      milesRange,
      isActive,
      transactions,
      loading,
      filter,
      isLastPage,
      isEmpty,
      isEmptyFilter,
      isEmptySearch,
      isProcessing,
      rewardCustomersIsEnabled,
      getListTransactions,
      cpf,
      setCpf,
    }),
    [
      setFilter,
      showMore,
      milesRange,
      isActive,
      transactions,
      loading,
      filter,
      isLastPage,
      isEmpty,
      isEmptyFilter,
      isEmptySearch,
      isProcessing,
      rewardCustomersIsEnabled,
      getListTransactions,
      cpf,
    ],
  );

  return <TransactionsContext.Provider value={value}>{children}</TransactionsContext.Provider>;
};

const useTransactions = () => {
  const transactions = useContext(TransactionsContext);

  if (!transactions) {
    throw new Error('Cannot use `useTransactions` outside of TransactionsProvider');
  }
  return transactions;
};

export { TransactionsContext, TransactionsProvider, useTransactions };
